import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { Loader2, TrendingUp, ArrowDownLeft, ArrowUpRight, Receipt, Smartphone, Landmark, Globe, Send, Banknote } from "lucide-react";

const CAT_META = {
  send: { icon: Send, color: "bg-rose-500" },
  request_pay: { icon: Send, color: "bg-rose-400" },
  bill: { icon: Receipt, color: "bg-amber-500" },
  recharge: { icon: Smartphone, color: "bg-sky-500" },
  bank_transfer: { icon: Landmark, color: "bg-indigo-500" },
  intl_transfer: { icon: Globe, color: "bg-violet-500" },
  financing_repay: { icon: Banknote, color: "bg-teal-500" },
};

export default function InsightsModal({ open, onClose }) {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("month");

  useEffect(() => {
    if (!open) return;
    setData(null);
    const month = scope === "month" ? new Date().toISOString().slice(0, 7) : "";
    api.get(`/insights${month ? `?month=${month}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  }, [open, scope]);

  const maxCat = data?.categories?.[0]?.amount || 1;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="insights-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" /> {t.insights}
          </DialogTitle>
          <DialogDescription>{t.insightsDesc}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 p-1 bg-muted rounded-2xl">
          {["month", "all"].map((s) => (
            <button
              key={s}
              data-testid={`insights-scope-${s}`}
              onClick={() => setScope(s)}
              className={`flex-1 h-9 rounded-xl text-sm font-bold transition-colors ${
                scope === s ? "bg-card shadow text-foreground" : "text-muted-foreground"
              }`}
            >
              {s === "month" ? t.thisMonth : t.allTime}
            </button>
          ))}
        </div>

        {!data ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-emerald-500/10 p-3.5">
                <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
                  <ArrowDownLeft className="w-3.5 h-3.5" /> {t.income}
                </div>
                <p data-testid="insights-income" className="text-lg font-black tabular-nums mt-1">{formatSAR(data.income, lang)}</p>
              </div>
              <div className="rounded-2xl bg-rose-500/10 p-3.5">
                <div className="flex items-center gap-1.5 text-rose-600 text-xs font-semibold">
                  <ArrowUpRight className="w-3.5 h-3.5" /> {t.spending}
                </div>
                <p data-testid="insights-spending" className="text-lg font-black tabular-nums mt-1">{formatSAR(data.spending, lang)}</p>
              </div>
            </div>

            <div className="flex items-center justify-between px-1 text-sm">
              <span className="text-muted-foreground">{t.net}</span>
              <span data-testid="insights-net" className={`font-bold tabular-nums ${data.net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {formatSAR(data.net, lang)} {t.currency}
              </span>
            </div>

            {data.categories.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">{t.noInsights}</p>
            ) : (
              <div className="space-y-3" data-testid="insights-categories">
                {data.categories.map((c) => {
                  const meta = CAT_META[c.type] || { icon: ArrowUpRight, color: "bg-slate-400" };
                  const Icon = meta.icon;
                  return (
                    <div key={c.type} data-testid={`insights-cat-${c.type}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          <span className={`w-7 h-7 rounded-xl grid place-items-center text-white ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          {t[`type_${c.type}`] || c.type}
                          <span className="text-xs text-muted-foreground">· {c.count}</span>
                        </span>
                        <span className="text-sm font-bold tabular-nums">{formatSAR(c.amount, lang)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full rounded-full ${meta.color}`} style={{ width: `${(c.amount / maxCat) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
